import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicReviewComponent } from '../public-review/public-review.component';
import { CustomRateComponent } from '../right-side-pannel/custom-rate/custom-rate.component';
import { NoResultsComponent } from '../no-results/no-results.component';
import { PublicProgramService } from '../../../api/external-web/public-program.service';
import { RepresentativeDetailsComponent } from '../right-side-pannel/representative-details/representative-details.component';
import { ChipFilterComponent } from '../chip-filters/chip-filter.component';
import { StaticDropdownComponent } from '@loup-transload-ui-app/common-components';

interface MonthYearItem {
  id: number;
  name: string;
}

@Component({
  selector: 'app-search-history-produce-rates',
  templateUrl: './search-history-produce-rates.component.html',
  styleUrls: ['./search-history-produce-rates.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NoResultsComponent,
    ChipFilterComponent,
    RepresentativeDetailsComponent,
    CustomRateComponent,
    PublicReviewComponent,
    StaticDropdownComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SearchHistoryProduceRatesComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly publicProgramService = inject(PublicProgramService);
  public producePath = 'assets/images/produce.png';
  public isServiceLoaded = false;
  destinationTransloadChips: any = [];
  originTransloadChips: any = [];
  selected: any;
  selectedType: any;
  @ViewChild('publicReviewProd') publicReviewProd: PublicReviewComponent;
  publicProgramData: any;
  public salesManagerDetails = '';
  public render = { origin: 0, destination: 0 };
  months: MonthYearItem[] = [];
  selectedMonth!: string;

  ngOnInit(): void {
    this.filterDropDown();
  }

  filterDropDown() {
    this.months = this.generateMonthYearList(24);
    const currentMonth = new Date().toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });
    this.selectedMonth =
      this.months.find((month) => month.name === currentMonth)?.name ??
      this.months[0]?.name;
    if (!this.selectedMonth) return;
    this.onDateSelected([this.selectedMonth]);
  }

  getTransloadChipsData(payload) {
    this.publicProgramService.searchPublicProgramRequest(payload).subscribe({
      next: (response: any) => {
        if (response && response.length > 0) {
          this.salesManagerDetails = response[0].commodityCategory?.attribute2;
          this.splitPrograms(response);
        } else {
          this.originTransloadChips = [];
          this.destinationTransloadChips = [];
          this.selected = null;
          this.render = { origin: 0, destination: 0 };
        }
      },
    });
  }

  private splitPrograms(programs: any): void {
    const unique = this.removeDuplicates(programs);
    const toChip = (p: any) => ({
      ...p,
      id: String(p.id),
      name: this.getCityState(p.transloadVendor),
    });
    const sortAZ = (a: any, b: any) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    this.originTransloadChips = unique
      .filter((p) => p.moveType === 'ORIGIN')
      .map(toChip)
      .sort(sortAZ);
    this.destinationTransloadChips = unique
      .filter((p) => p.moveType === 'DESTINATION')
      .map(toChip)
      .sort(sortAZ);
    this.applyDefaultSelection();
    this.cdr.markForCheck();
  }

  /** Pick default chip: first Origin if available, else first Destination */
  private applyDefaultSelection(): void {
    const hasOrigin = this.originTransloadChips.length > 0;
    const hasDest = this.destinationTransloadChips.length > 0;
    if (!hasOrigin && !hasDest) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('origin');
      this.reload('destination');
      return;
    }
    if (hasOrigin) {
      const first = this.originTransloadChips[0];
      this.selected = first;
      this.selectedType = 'Origin';
      this.publicProgramData = first;
      this.reload('destination');
    } else {
      const first = this.destinationTransloadChips[0];
      this.selected = first;
      this.selectedType = 'Destination';
      this.publicProgramData = first;
      this.reload('origin');
    }
    this.refreshReviewDebounced();
  }

  private removeDuplicates(array: any) {
    const seen = new Set<number | string>();
    return array.filter((item) => {
      const key = item.id;
      const duplicate = seen.has(key);
      seen.add(key);
      return !duplicate;
    });
  }

  private getCityState(vendor?: { city?: string; state?: string }): string {
    const city = vendor?.city ?? '';
    const state = vendor?.state ?? '';
    return city && state ? `${city}, ${state}` : city || state;
  }

  /** Origin chip selection */
  public onSelectOrigin(item: any | null): void {
    if (!item) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('destination');
      this.refreshReviewDebounced();
      return;
    }
    this.selected = item;
    this.selectedType = 'Origin';
    this.publicProgramData = item;
    this.reload('destination');
    this.refreshReviewDebounced();
  }

  /** Destination chip selection */
  public onSelectDestination(item: any | null): void {
    if (!item) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('origin');
      this.refreshReviewDebounced();
      return;
    }
    this.selected = item;
    this.selectedType = 'Destination';
    this.publicProgramData = item;
    this.reload('origin');
    this.refreshReviewDebounced();
  }

  /** Flip 0/1 to force @if branches to switch -> component instance is recreated */
  private reload(group: 'origin' | 'destination'): void {
    this.render = { ...this.render, [group]: this.render[group] ^ 1 };
    this.cdr.markForCheck();
  }

  /** Keep your existing behavior */
  private refreshReviewDebounced(): void {
    setTimeout(() => {
      if (this.publicReviewProd) {
        this.publicReviewProd.refresh(this.publicProgramData);
        this.cdr.markForCheck();
      }
    }, 400);
    this.cdr.markForCheck();
  }

  generateMonthYearList(
    monthsBack = 24,
    baseDate: Date = new Date()
  ): MonthYearItem[] {
    const result: MonthYearItem[] = [];
    const startMonthOffset = baseDate.getDate() >= 15 ? 1 : 0;
    const date = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth() + startMonthOffset,
      1
    );
    for (let i = 0; i < monthsBack; i++) {
      const monthYear = date.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      });
      result.push({
        id: i + 1,
        name: monthYear,
      });
      date.setMonth(date.getMonth() - 1);
    }
    return result;
  }

  onDateSelected(value: any): void {
    if (!value || !value.length) return;
    const selectedMonthYear = value[0];
    const { programStartDate, programEndDate } =
      this.getDateRangeFromSelection(selectedMonthYear);
    const payload = {
      monthYear: selectedMonthYear,
      programStartDate,
      programEndDate,
      programType: 'Produce',
    };
    this.getTransloadChipsData(payload);
  }

  getDateRangeFromSelection(selection: string): {
    programStartDate: string;
    programEndDate: string;
  } {
    const parts = selection.trim().split(' ');
    const [monthName, yearStr] = parts;
    const year = Number(yearStr);
    const monthIndex = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ].findIndex((m) => m.toLowerCase() === monthName.toLowerCase());
    if (monthIndex === -1) {
      throw new Error(`Invalid month name: ${monthName}`);
    }
    const programStartDate = new Date(Date.UTC(year, monthIndex, 1));
    const programEndDate = new Date(Date.UTC(year, monthIndex + 1, 0));
    const pad = (n: number) => n.toString().padStart(2, '0');
    return {
      programStartDate: `${programStartDate.getUTCFullYear()}-${pad(
        programStartDate.getUTCMonth() + 1
      )}-${pad(programStartDate.getUTCDate())}`,
      programEndDate: `${programEndDate.getUTCFullYear()}-${pad(
        programEndDate.getUTCMonth() + 1
      )}-${pad(programEndDate.getUTCDate())}`,
    };
  }
}
